import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  CardHeader,
  ClickAwayListener,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import FilterIcon from '@material-ui/icons/FilterList';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';

import * as actions from '../../../../state';
import styles from '../styles.module.css';
import {
  checkUserPermission,
  getAvatar,
} from '../../../../utils';
import ChartCardContent from './ChartCardContent';
import ChartCardMenu from './ChartCardMenu';
import ChartCardPlaceholder from './ChartCardPlaceholder';
import ChartEditOverlay from './ChartEditOverlay';

const messages = defineMessages({
  FILTERS: {
    id: 'FILTERS',
    defaultMessage: 'Filters',
  },
  EDIT_CHART: {
    id: 'EDIT_CHART',
    defaultMessage: 'Edit Chart',
  },
  COPY_CHART: {
    id: 'COPY_CHART',
    defaultMessage: 'Copy Chart',
  },
  CHART_FILTERS: {
    id: 'CHART_FILTERS',
    defaultMessage: 'Chart Filters',
  },
  DELETE_CHART: {
    id: 'DELETE_CHART',
    defaultMessage: 'Delete Chart',
  },
  CURRENT_FILTERS: {
    id: 'CURRENT_FILTERS',
    defaultMessage: 'Filters',
  },
  FILTER_ICON_TOOLTIP: {
    id: 'FILTER_ICON_TOOLTIP',
    defaultMessage: 'Edit data configuration',
  },
  MY_SUBSCRIBER_FILTER: {
    id: 'MY_SUBSCRIBER_FILTER',
    defaultMessage: 'Showing data of logged in user',
  },
  SOCKET_LOST_CONNECTION_CHART: {
    id: 'SOCKET_LOST_CONNECTION_CHART',
    defaultMessage: 'Lost connection. Trying to reconnect...',
  },
  SOCKET_NO_CONNECTION_CHART: {
    id: 'SOCKET_NO_CONNECTION_CHART',
    defaultMessage: 'No connection. Trying to reconnect...',
  },
});

function ChartCard({
  chart,
  editChart,
  deleteChart,
  renderRequired,
}) {
  const isNetworkConnected = useSelector(actions.selectNetworkConnected);
  const isSocketConnected = useSelector(actions.selectIsConnected);
  const layout = useSelector(actions.selectLayout);
  const selectedChart = useSelector(actions.readStateSelectedChart);
  const socket = useSelector(actions.selectSocket);
  const user = useSelector(actions.readStateUser);

  const [connectionAnchor, setConnectionAnchor] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState(null);

  const urlPlaceholder = () => (
    <div className={`animate__animated animate__fadeIn ${styles.cardPlaceholder}`}>
      <Icon className={styles.newcardIcon}>
        show_chart
      </Icon>

      <p style={{ color: 'rgba(0,0,0,0.56)' }}>Thank you Kanye. Very cool!</p>
    </div>
  );

  const connectionPopover = (event) => {
    const anchor = !connectionAnchor && event.currentTarget.tagName
      ? event.currentTarget
      : null;
    setConnectionAnchor(anchor);
  };

  const togglePopover = (event) => {
    const anchor = !popoverAnchor && event.currentTarget.tagName
      ? event.currentTarget
      : null;
    setPopoverAnchor(anchor);
  };

  const renderFilterIcon = (filterList, iconBtn) => {
    const button = iconBtn ? (
      <Tooltip key={`menu_${chart.id}`} disableFocusListener title={<FormattedMessage {...messages.FILTERS} />}>
        <IconButton
          aria-label={<FormattedMessage {...messages.EDIT_CHART} />}
          onClick={togglePopover}
          disableRipple
        >
          <FilterIcon />
        </IconButton>
      </Tooltip>
    ) : (
      <Tooltip key={`menu_${chart.id}`} disableFocusListener title={<FormattedMessage {...messages.FILTERS} />}>
        <IconButton
          className={styles.cardMenuBtn}
          style={{ padding: '6px 8px' }}
          onClick={togglePopover}
          disableRipple
        >
          <FilterIcon />
        </IconButton>
      </Tooltip>
    );

    return (
      <>
        <ClickAwayListener onClickAway={togglePopover}>
          {button}
        </ClickAwayListener>
        <Popover
          className={styles.filterPopover}
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <div className={styles.filterPopoverContent}>
            <h4 style={{ margin: 0, paddingTop: 0 }}>
              <FormattedMessage {...messages.CURRENT_FILTERS} />
            </h4>

            <List>
              {filterList.map((filter, fId) => {
                if (filter.type === 'my_subscriber') {
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <ListItem key={`filter1_${fId}`} className={styles.filterPopoverFilterRow}>
                      <Typography>
                        <FormattedMessage {...messages.MY_SUBSCRIBER_FILTER} />
                      </Typography>
                    </ListItem>
                  );
                }
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <ListItem key={`filter2_${fId}`} className={styles.filterPopoverFilterRow}>
                    <ListItemAvatar>
                      {getAvatar(filter, styles)}
                    </ListItemAvatar>

                    <ListItemText
                      primary={filter.name || filter.value || ' '}
                      secondary={filter.desc}
                      className={styles.filterText}
                    />
                  </ListItem>
                );
              })}
            </List>

            {!layout.viewOnly && checkUserPermission(user, 'EDIT_BOARDS') && (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
              <div
                onClick={editChart}
                className={styles.filterPopoverAction}
                style={{ color: layout.brandPrimaryColor }}
              >
                <FormattedMessage {...messages.FILTER_ICON_TOOLTIP} />
              </div>
            )}
          </div>
        </Popover>
      </>
    );
  };

  const getFilterBtn = (iconBtn) => {
    // if there are sources, there may be filters to surface in the header actions
    if (chart.data_sources && chart.data_sources.length) {
      let filterList = [];

      chart.data_sources.forEach((source) => { // look for filters
        if (source.filters) {
          filterList = filterList.concat(source.filters);
        }
      });

      if (filterList.length) { // if there were filters found
        return renderFilterIcon(filterList, iconBtn);
      }
    }

    return null;
  };

  const renderNetworkIcon = () => {
    let msg;
    if (isNetworkConnected === false) {
      msg = messages.SOCKET_LOST_CONNECTION_CHART.id;
    } else {
      let hasConnections = false;
      if (chart.data_sources && chart.data_sources.length) {
        chart.data_sources.forEach((src) => {
          if (src.type === 'api' && src.socketSubscriptions && src.socketSubscriptions.length) {
            hasConnections = true;
          } else if (src.type === 'socket') {
            hasConnections = true;
          }
        });
      }

      if (!hasConnections || isSocketConnected) {
        return '';
      }

      if (isSocketConnected === false && socket === null) {
        msg = messages.SOCKET_NO_CONNECTION_CHART.id;
      } else {
        msg = messages.SOCKET_LOST_CONNECTION_CHART.id;
      }
    }

    return (
      <>
        <ClickAwayListener onClickAway={connectionPopover}>
          <div style={{ flex: 1, textAlign: 'right' }}>
            <IconButton style={{ padding: 0 }} onClick={connectionPopover}>
              <ErrorOutline color="error" />
            </IconButton>
          </div>
        </ClickAwayListener>
        <Popover
          open={Boolean(connectionAnchor)}
          anchorEl={connectionAnchor}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <div style={{ padding: 8, color: 'rgba(0,0,0,.54)' }}>
            <FormattedMessage {...messages[msg]} />
          </div>
        </Popover>
      </>
    );
  };

  const noSourcesAdded = !chart.data_sources || !chart.data_sources.length;

  const headerActions = [];

  const filterBtn = getFilterBtn();
  if (filterBtn) {
    headerActions.push(filterBtn);
  }

  if (!layout.publicMode && !layout.editMode && !layout.viewOnly) {
    headerActions.push(
      <ChartCardMenu
        key={`menu_${chart.id}`}
        chart={chart}
        allowCopy
        getFilterBtn={getFilterBtn}
        editChart={editChart}
        deleteChart={deleteChart}
      />,
    );
  }

  const headerStyling = chart.properties.cardHeaderFontSize
    ? { fontSize: chart.properties.cardHeaderFontSize } : {};

  if (noSourcesAdded || (chart.data_sources[0].stat === 'custom' && !chart.data_sources[0].queryParams.url)) { // if there aren't any data sources, render a placeholder
    return (
      <>
        <ChartCardPlaceholder
          chart={chart}
          editChart={editChart}
          deleteChart={deleteChart}
        />

        { layout.editMode && chart.type !== 'new' && selectedChart?.id !== chart.id
          && (
            <ChartEditOverlay
              chart={chart}
              allowCopy={false}
              getFilterBtn={getFilterBtn}
              editChart={editChart}
              deleteChart={deleteChart}
            />
          )}
      </>
    );
  } if (chart.invalidUrl) {
    return urlPlaceholder();
  }

  return (
    <>
      <CardHeader
        key={chart.id}
        {...(headerActions.length ? { action: headerActions } : {})}
        title={(
          <div>
            <div style={headerStyling} className={`${styles.chartTitle}`}>
              {chart.name}
            </div>
            {renderNetworkIcon()}
          </div>
        )}
        classes={{
          title: styles.cardTitle,
          root: styles.cardHeader,
          content: styles.cardContent,
          action: styles.cardAction,
        }}
      />

      <ChartCardContent
        editMode={layout.editMode}
        rendering={renderRequired}
        chart={chart}
      />

      { layout.editMode && selectedChart?.id !== chart.id
        && (
          <ChartEditOverlay
            chart={chart}
            allowCopy
            getFilterBtn={getFilterBtn}
            editChart={editChart}
            deleteChart={deleteChart}
          />
        )}
    </>
  );
}

ChartCard.defaultProps = {
  renderRequired: false,
};

ChartCard.propTypes = {
  chart: PropTypes.shape({}).isRequired,
  editChart: PropTypes.func.isRequired,
  deleteChart: PropTypes.func.isRequired,
  renderRequired: PropTypes.bool,
};

export default injectIntl(ChartCard);
